import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, get, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismButton,
  PrismIcon,
  PrismLoadingBar,
  PrismPerfectScrollbar,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import { ISSUE_TYPES } from 'lumen/models/parity-manager/issue';

const ATTACHMENT_IDS = {
  [ISSUE_TYPES.MAJOR_OTA]: [
    'base_screenshot',
    'channel_screenshot',
    'extranet_screenshot',
    'additional_screenshot',
  ],
  [ISSUE_TYPES.TEST_BOOKING]: [
    'brand_screenshot',
    'brand_final_checkout_page',
    'channel_final_checkout_page',
    'metasearch_screenshot',
    'booking_reservation_file',
    'cancellation_proof',
    'cancellation_policy',
    'booking_summary',
  ],
  [ISSUE_TYPES.BAIT_AND_SWITCH]: [
    'brand_rate_screenshot',
    'meta_rate_screenshot',
    'displayed_channel_rate_screenshot',
    'actual_channel_rate_screenshot',
  ],
};

const REQUIRED_ATTACHMENT_IDS = {
  [ISSUE_TYPES.MAJOR_OTA]: ['base_screenshot', 'channel_screenshot'],
  [ISSUE_TYPES.TEST_BOOKING]: [
    'brand_screenshot',
    'channel_final_checkout_page',
    'booking_reservation_file',
  ],
  [ISSUE_TYPES.BAIT_AND_SWITCH]: [
    'meta_rate_screenshot',
    'displayed_channel_rate_screenshot',
    'actual_channel_rate_screenshot',
  ],
};

// Max allowed size of attachments of 10MB (= 10485760 bytes)
const MAX_ISSUE_ATTACHMENT_FILE_SIZE = 10485760;

interface ParityParityManagerAttachmentFormSignature {
  Args: {
    back: unknown;
    parentController: unknown;
    saveIssue: unknown;
    uploadingIssue: unknown;
  };
}

export default class ParityParityManagerAttachmentForm extends Component<ParityParityManagerAttachmentFormSignature> {
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get attachmentDetails() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const attachmentIds =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      ATTACHMENT_IDS[this.args.parentController.selectedMetric];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const requiredAttachmentIds =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      REQUIRED_ATTACHMENT_IDS[this.args.parentController.selectedMetric];
    // @ts-expect-error: Incorrect type
    const attachmentDetails = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    attachmentIds.forEach((attachmentId) => {
      attachmentDetails.push({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        id: attachmentId,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        required: requiredAttachmentIds.indexOf(attachmentId) !== -1,
        translationKey: `parity_manager.attachments.${attachmentId}`,
        tagName: `attachment-form-file-${attachmentId}`,
        file: get(this, `args.issue.${attachmentId}`),
        url: get(this, `args.issue.${attachmentId}_url`),
        name: get(this, `args.issue.${attachmentId}_name`),
        timestamp: get(this, `args.issue.${attachmentId}_timestamp`),
      });
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return attachmentDetails;
  }

  // Base and channel screenshot are required
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormInvalid() {
    // @ts-expect-error: Incorrect type
    if (this.args.parentController.isMajorOtaView) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const {
        base_screenshot: baseScreenshot,
        channel_screenshot: channelScreenshot,
        // @ts-expect-error: Incorrect type
      } = this.args.issue;
      return this.args.uploadingIssue || !baseScreenshot || !channelScreenshot;
    }

    // @ts-expect-error: Incorrect type
    if (this.args.parentController.isTestBookingView) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const {
        brand_screenshot: brandScreenshot,
        channel_final_checkout_page: channelFinalCheckoutPage,
        booking_reservation_file: bookingReservationFile,
        // @ts-expect-error: Incorrect type
      } = this.args.issue;

      return (
        this.args.uploadingIssue ||
        !brandScreenshot ||
        !channelFinalCheckoutPage ||
        !bookingReservationFile
      );
    }

    // @ts-expect-error: Incorrect type
    if (this.args.parentController.isBaitAndSwitchView) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const {
        meta_rate_screenshot: metaRateScreenshot,
        displayed_channel_rate_screenshot: displayedChannelRateScreenshot,
        actual_channel_rate_screenshot: actualChannelRateScreenshot,
        // @ts-expect-error: Incorrect type
      } = this.args.issue;

      return (
        this.args.uploadingIssue ||
        !metaRateScreenshot ||
        !displayedChannelRateScreenshot ||
        !actualChannelRateScreenshot
      );
    }

    return false;
  }

  setScreenshots(): void {
    // @ts-expect-error: Incorrect type
    const formFileInputs = document
      .getElementById('attachment-form')
      .querySelectorAll('input.attachment-form-file');
    let areAllSelectedFilesWithinMaxFileSize = true;

    if (formFileInputs.length) {
      formFileInputs.forEach((fileInput) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const file = fileInput.files[0];
        if (file) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (file.size < MAX_ISSUE_ATTACHMENT_FILE_SIZE) {
            const dataName = fileInput.getAttribute('data-name'); // eg "base_screenshot"
            set(this, `args.issue.${dataName}`, file);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            set(this, `args.issue.${dataName}_name`, file.name);

            // Create an url to download/view the file again before submitting
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const baseUrl = URL.createObjectURL(file);
            set(this, `args.issue.${dataName}_url`, baseUrl);

            const localNow = this.hotelAndMarketService.getLocalTime();
            set(
              this,
              `args.issue.${dataName}_timestamp`,
              localNow.format('L LTS'),
            );
          } else {
            areAllSelectedFilesWithinMaxFileSize = false;
          }
        }
      });
    }
    set(
      this,
      'args.issue.areAllSelectedFilesWithinMaxFileSize',
      areAllSelectedFilesWithinMaxFileSize,
    );
  }

  @action
  // @ts-expect-error: Incorrect type
  checkFileSize(event): void {
    // This is fired in the onchange of the file inputs in the form of the issue upload modal
    // Main purpose is to show feedback to the user if they select files that exceed the limit
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (event.srcElement.files && event.srcElement.files[0]) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (event.srcElement.files[0].size > MAX_ISSUE_ATTACHMENT_FILE_SIZE) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showWarningAlert({
          title: this.intl.t('general.error_title'),
          text: this.intl.t(
            'parity_manager.issue.upload.error.file_size_exceeded',
          ),
        });

        // Clear the value of the file input
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        event.srcElement.value = '';
      } else {
        this.setScreenshots();
      }
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  clickFileBrowserInput(id): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    document.getElementById(id).click();
  }

  @action
  // @ts-expect-error: Incorrect type
  deleteAttachment(fileName): void {
    // @ts-expect-error: Incorrect type
    document.getElementById(`attachment-form-file-${fileName}`).value = null;
    set(this, `args.issue.${fileName}`, null);
    set(this, `args.issue.${fileName}_name`, null);
    set(this, `args.issue.${fileName}_url`, null);
    set(this, `args.issue.${fileName}_timestamp`, null);
  }

  @action
  // @ts-expect-error: Incorrect type
  downloadAttachment(attachment): void {
    // IE10+ : (has Blob, but not a[download] or URL)
    // https://stackoverflow.com/questions/27463901/setting-window-location-or-window-open-in-angularjs-gives-access-is-denied-in
    // @ts-expect-error: Incorrect type
    if (navigator.msSaveBlob) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      window.navigator.msSaveOrOpenBlob(attachment.file, attachment.name);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      openUrl(attachment.url);
    }
  }

  <template>
    {{#if @uploadingIssue}}
      <div class="relative">
        <PrismLoadingBar />
      </div>
    {{/if}}
    <form
      class="pt-500 pr-500 pl-500"
      enctype="multipart/form-data"
      id="attachment-form"
      method="post"
      name="attachment-form"
    >
      <h2 class="header-title">
        {{t "parity_manager.attachments.file_attachments"}}
      </h2>
      <div class="text-200--regular pt-400">
        {{t "parity_manager.issue.upload.info.message"}}
      </div>
      <div class="attachments mt-400">
        <PrismPerfectScrollbar
          class="attachment-form__attachments-scrollable-container"
        >
          {{#each this.attachmentDetails as |attachment|}}
            <div>
              <div class="flex w-full flex-col">
                {{t attachment.translationKey}}
                {{#if attachment.required}}*{{/if}}
              </div>
              <div class="mt-200 flex flex-row">
                {{#if attachment.name}}
                  <div class="mr-200 mb-200">
                    <PrismBadge @label={{attachment.name}} />
                  </div>
                  <button
                    class="button--unstyled mb-200
                      {{unless attachment.url 'disabled'}}"
                    type="button"
                    {{on "click" (fn this.deleteAttachment attachment.id)}}
                  >
                    <PrismIcon @name="bin" @size="small" />
                  </button>
                {{/if}}
              </div>
              <div class="flex w-full flex-col">
                <div class="action-button-container">
                  <div class="action-button">
                    <input
                      accept=".png"
                      class="attachment-form-file hidden-file-input"
                      data-name={{attachment.id}}
                      hidden
                      id={{attachment.tagName}}
                      name={{attachment.tagName}}
                      type="file"
                      {{on "change" this.checkFileSize}}
                    />
                    <div class="mb-400">
                      <PrismButton
                        @buttonType="secondary"
                        @iconBefore="upload"
                        @label={{t "parity_manager.attachments.attach_file"}}
                        @onClick={{fn
                          this.clickFileBrowserInput
                          attachment.tagName
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          {{/each}}
        </PrismPerfectScrollbar>
      </div>
    </form>

    <div class="settings-modal__footer mt-400 flex flex-row justify-end">
      <GeneralFormFieldsCancelAndConfirm
        {{! @glint-expect-error: Incorrect type }}
        @cancel={{@back}}
        @cancelText={{t "general.back"}}
        {{! @glint-expect-error: Incorrect type }}
        @confirm={{@saveIssue}}
        @confirmText={{t "general.submit_issue"}}
        {{! @glint-expect-error: Incorrect type }}
        @isConfirmDisabled={{this.isFormInvalid}}
      />
    </div>
  </template>
}
